/**
 * AIChatProvider — Phase 9D.2
 *
 * Context provider that tracks:
 *   - Current page path
 *   - Current entity being viewed (customer, account, etc.)
 *   - Chat open/closed state
 *
 * Components use useAIChatContext() to read context and toggle the panel.
 */

import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import type { AIChatContext } from '../../api/hooks/use-ai-chat';

// ─── Context Shape ───────────────────────────────────────────────────────────

interface AIChatContextValue {
  chatOpen: boolean;
  toggleChat: () => void;
  openChat: () => void;
  closeChat: () => void;
  context: AIChatContext;
  setEntityContext: (entity: { type?: string; id?: string; name?: string }) => void;
  clearEntityContext: () => void;
}

const AIChatCtx = createContext<AIChatContextValue | null>(null);

// ─── Path → Page Label ──────────────────────────────────────────────────────

function pageLabel(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return 'Dashboard';

  const labels: Record<string, string> = {
    dashboard: 'Dashboard',
    customers: 'Customers',
    loans: 'Loans',
    mortgages: 'Mortgages',
    compliance: 'Compliance',
    payments: 'Payments',
    'payment-ops': 'Payment Operations',
    'general-ledger': 'General Ledger',
    kyc: 'KYC Management',
    audit: 'Audit',
    settings: 'Settings',
    institutions: 'Institutions',
    users: 'Users',
    accounts: 'Accounts',
    branch: 'Branch Banking',
    rewards: 'Rewards',
    insights: 'Insights',
  };

  return labels[segments[0]] ?? segments[0].replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// ─── Detect entity from route params ─────────────────────────────────────────

function detectEntity(pathname: string): { type?: string; id?: string } {
  const customerMatch = pathname.match(/\/customers\/([^/]+)/);
  if (customerMatch) return { type: 'customer', id: customerMatch[1] };

  const accountMatch = pathname.match(/\/accounts\/([^/]+)/);
  if (accountMatch) return { type: 'account', id: accountMatch[1] };

  const loanMatch = pathname.match(/\/loans\/applications\/([^/]+)/);
  if (loanMatch) return { type: 'loan', id: loanMatch[1] };

  const mortgageMatch = pathname.match(/\/mortgages\/([^/]+)$/);
  if (mortgageMatch && mortgageMatch[1] !== 'pipeline' && mortgageMatch[1] !== 'products') {
    return { type: 'mortgage', id: mortgageMatch[1] };
  }

  return {};
}

// ─── Provider ────────────────────────────────────────────────────────────────

export const AIChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [chatOpen, setChatOpen] = useState(false);
  const [entityOverride, setEntityOverride] = useState<{ type?: string; id?: string; name?: string }>({});

  // Auto-detect entity from URL
  const detectedEntity = useMemo(() => detectEntity(location.pathname), [location.pathname]);

  // Clear entity override on navigation
  useEffect(() => {
    setEntityOverride({});
  }, [location.pathname]);

  const context: AIChatContext = useMemo(() => ({
    currentPage: pageLabel(location.pathname),
    currentEntityType: entityOverride.type ?? detectedEntity.type,
    currentEntityId: entityOverride.id ?? detectedEntity.id,
    currentEntityName: entityOverride.name,
  }), [location.pathname, detectedEntity, entityOverride]);

  const toggleChat = useCallback(() => setChatOpen(prev => !prev), []);
  const openChat = useCallback(() => setChatOpen(true), []);
  const closeChat = useCallback(() => setChatOpen(false), []);

  const setEntityContext = useCallback((entity: { type?: string; id?: string; name?: string }) => {
    setEntityOverride(entity);
  }, []);

  const clearEntityContext = useCallback(() => {
    setEntityOverride({});
  }, []);

  const value = useMemo(() => ({
    chatOpen, toggleChat, openChat, closeChat,
    context, setEntityContext, clearEntityContext,
  }), [chatOpen, toggleChat, openChat, closeChat, context, setEntityContext, clearEntityContext]);

  return <AIChatCtx.Provider value={value}>{children}</AIChatCtx.Provider>;
};

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useAIChatContext(): AIChatContextValue {
  const ctx = useContext(AIChatCtx);
  if (!ctx) {
    throw new Error('useAIChatContext must be used within an AIChatProvider');
  }
  return ctx;
}

export default AIChatProvider;
